import React, { useState, useEffect, useCallback } from 'react';
import { X, Share2, Copy, Check, Clock, Heart, ExternalLink, Calendar, CalendarDays, FileText, Smartphone, Monitor, ArrowRightLeft, Plus, Trash2, RefreshCw, Bandage, KeyRound, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { MOOD_THEMES } from '../../constants/mediscribe';
import { copyToClipboard } from '../../hooks/useMediscribe';
import NSISymptomTracker from './NSISymptomTracker';
import { announceProfileSaved } from './ProfileSyncBadge';
import { MyCorrectionsSection } from './MyCorrectionsSection';
import { persistProfilePref } from '../../utils/persistProfilePref';

// ─────────────────────────────────────────────────────────────────────
// Feb 28, 2026 (v6.8.44) — SavedInjuriesEditor
// ─────────────────────────────────────────────────────────────────────
// Inline editor for the user's stored medical-history list. Each entry:
//   { id, label, year, active }
// Persisted server-side via /api/auth/profile (saved_injuries field).
// Used in patient mode for disability rewrites & appeals where the same
// conditions recur across many letter requests.
const SavedInjuriesEditor = ({ savedInjuries, setSavedInjuries }) => {
  const [draftLabel, setDraftLabel] = useState('');
  const [draftYear, setDraftYear] = useState('');
  const list = Array.isArray(savedInjuries) ? savedInjuries : [];

  const addInjury = () => {
    const label = draftLabel.trim();
    if (!label) return;
    const next = [
      ...list,
      {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `inj-${Date.now()}`,
        label: label.slice(0, 140),
        year: draftYear.trim().slice(0, 10),
        active: true,
      },
    ];
    setSavedInjuries && setSavedInjuries(next);
    setDraftLabel('');
    setDraftYear('');
  };

  const toggleActive = (id) => {
    setSavedInjuries && setSavedInjuries(list.map(i => i.id === id ? { ...i, active: !i.active } : i));
  };

  const removeInjury = (id) => {
    setSavedInjuries && setSavedInjuries(list.filter(i => i.id !== id));
  };

  return (
    <div className="space-y-3 p-3 bg-rose-500/5 border border-rose-500/20 rounded-lg" data-testid="saved-injuries-editor">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-rose-300">
          <Bandage className="w-4 h-4" />
          My Medical History / Injuries
          <span className="text-[10px] px-2 py-0.5 bg-rose-500/15 text-rose-300 rounded font-bold">NEW</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{list.length} saved</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Save your past conditions (e.g., <em>L MCL sprain, 2024</em> &middot; <em>L5-S1 disc herniation, 2022</em>). When you draft a letter, you&apos;ll see a one-tap row above the situation textbox to inject any saved item — perfect for disability rewrites and appeals where the same diagnoses keep coming back. Mark items <strong>active</strong> for current flare-ups; inactive ones still save but render dimmer.
      </p>

      {/* Existing list */}
      {list.length > 0 && (
        <div className="space-y-1.5">
          {list.map((inj) => (
            <div
              key={inj.id}
              className={`flex items-center gap-2 p-2 rounded-md border ${inj.active ? 'border-rose-500/30 bg-rose-500/5' : 'border-border/40 bg-secondary/30 opacity-70'}`}
              data-testid={`saved-injury-${inj.id}`}
            >
              <button
                type="button"
                onClick={() => toggleActive(inj.id)}
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold shrink-0 ${inj.active ? 'bg-rose-500/20 text-rose-300' : 'bg-muted text-muted-foreground'}`}
                data-testid={`saved-injury-${inj.id}-toggle`}
                title="Toggle active flare"
              >
                {inj.active ? 'ACTIVE' : 'inactive'}
              </button>
              <span className="text-sm text-foreground flex-1 min-w-0 truncate">
                {inj.label}
                {inj.year && <span className="text-muted-foreground ml-2 text-xs">({inj.year})</span>}
              </span>
              <button
                type="button"
                onClick={() => removeInjury(inj.id)}
                className="text-muted-foreground hover:text-rose-400 transition-colors shrink-0"
                data-testid={`saved-injury-${inj.id}-remove`}
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      <div className="flex items-end gap-2 pt-1">
        <div className="flex-1 min-w-0 space-y-1">
          <Label className="text-xs">Condition / Injury</Label>
          <input
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInjury(); } }}
            placeholder="e.g., L5-S1 disc herniation"
            className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
            data-testid="saved-injury-label-input"
            maxLength={140}
          />
        </div>
        <div className="w-24 space-y-1">
          <Label className="text-xs">Year</Label>
          <input
            value={draftYear}
            onChange={(e) => setDraftYear(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInjury(); } }}
            placeholder="2024"
            className="w-full h-9 px-2 rounded-md border border-border bg-input text-sm"
            data-testid="saved-injury-year-input"
            maxLength={10}
          />
        </div>
        <Button
          type="button"
          onClick={addInjury}
          size="sm"
          disabled={!draftLabel.trim()}
          className="h-9 px-3"
          data-testid="saved-injury-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
};

// Share Duration Options (how long link stays active)
const SHARE_DURATIONS = [
  { value: 24, label: '24 hours', description: 'Quick clinic day' },
  { value: 72, label: '72 hours', description: 'Extended coverage' },
  { value: 168, label: '1 week', description: 'Backlog catchup' }
];

// History Date Range Options (how far back to show)
const DATE_RANGE_OPTIONS = [
  { value: 1, label: 'Today', description: 'Current day only' },
  { value: 3, label: '3 Days', description: 'Recent patients' },
  { value: 7, label: '7 Days', description: 'Past week' },
  { value: 14, label: '2 Weeks', description: 'Extended backlog' }
];

// ──────────────────────────────────────────────────────────────────────────
// Feb 28, 2026 — Profile preference toggles that need to:
//   (1) Stay reactive to a `writeright:profile-synced` window event (so the
//       /api/auth/me sync hydrates the visual state without manual re-toggle).
//   (2) Fire-and-forget PUT to /api/auth/profile so the preference is
//       persisted in MongoDB and survives device changes / logouts.
// Extracted as proper components so React hooks live at component top-level.
// ──────────────────────────────────────────────────────────────────────────

const GenesisDotPhrasesToggle = () => {
  const [dotOn, setDotOn] = useState(
    () => localStorage.getItem('writeright_use_genesis_dot_phrases') === 'true'
  );
  useEffect(() => {
    const sync = () => {
      const v = localStorage.getItem('writeright_use_genesis_dot_phrases') === 'true';
      setDotOn(v);
    };
    window.addEventListener('storage', sync);
    window.addEventListener('writeright:profile-synced', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('writeright:profile-synced', sync);
    };
  }, []);
  return (
    <Switch
      checked={dotOn}
      onCheckedChange={(checked) => {
        setDotOn(checked);
        localStorage.setItem('writeright_use_genesis_dot_phrases', checked ? 'true' : 'false');
        const API = window.__WR_API_BASE__;
        if (API) {
          axios.put(
            `${API}/api/auth/profile`,
            { use_genesis_dot_phrases: checked },
            { withCredentials: true }
          ).then(() => announceProfileSaved(true))
          .catch((err) => {
            if (err?.response?.status >= 500) {
              console.warn('Dot-phrase pref save failed:', err.message);
            }
            announceProfileSaved(false);
          });
        }
        toast.success(checked
          ? 'Summary will start with .name dot-phrase'
          : 'Summary will start with Patient is a ...',
          { duration: 1800 });
      }}
      data-testid="genesis-dot-phrases-toggle"
    />
  );
};

const AutoStreamToggle = () => {
  const [streamOn, setStreamOn] = useState(
    () => localStorage.getItem('writeright_auto_stream_default') === 'true'
  );
  useEffect(() => {
    const sync = () => {
      const v = localStorage.getItem('writeright_auto_stream_default') === 'true';
      setStreamOn(v);
    };
    window.addEventListener('storage', sync);
    window.addEventListener('writeright:profile-synced', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('writeright:profile-synced', sync);
    };
  }, []);
  return (
    <label
      className={`relative inline-flex items-center cursor-pointer rounded-full border-2 transition-all ${
        streamOn
          ? 'border-emerald-400 bg-emerald-600'
          : 'border-slate-500 bg-slate-700'
      }`}
      style={{ width: 44, height: 24 }}
      data-testid="auto-stream-toggle-wrapper"
    >
      <input
        type="checkbox"
        checked={streamOn}
        onChange={(e) => {
          const checked = e.target.checked;
          setStreamOn(checked);
          localStorage.setItem('writeright_auto_stream_default', checked ? 'true' : 'false');
          localStorage.setItem('writeright_auto_share_drafts', checked ? 'true' : 'false');
          if (checked) {
            const activeTeam = localStorage.getItem('writeright_active_team');
            const existing = localStorage.getItem('writeright_auto_share_team');
            if (activeTeam && !existing) {
              localStorage.setItem('writeright_auto_share_team', activeTeam);
            }
          }
          const API = window.__WR_API_BASE__;
          if (API) {
            axios.put(
              `${API}/api/auth/profile`,
              { default_auto_stream: checked },
              { withCredentials: true }
            ).then(() => announceProfileSaved(true))
            .catch((err) => {
              if (err?.response?.status >= 500) {
                console.warn('Auto-stream pref save failed:', err.message);
              }
              announceProfileSaved(false);
            });
          }
          // Notify other hooks (useAutosaveSession) so the team-share React
          // state updates immediately without waiting for a window event.
          try {
            window.dispatchEvent(new CustomEvent('writeright:profile-synced', {
              detail: { default_auto_stream: checked }
            }));
          } catch { /* noop */ }
          toast.success(checked ? 'Auto-stream enabled — notes will silently share to all your teams' : 'Auto-stream disabled', { duration: 1800 });
        }}
        className="sr-only"
        data-testid="auto-stream-toggle"
      />
      <span
        className={`absolute top-0.5 inline-block w-4 h-4 rounded-full bg-white shadow-md transition-transform ${
          streamOn ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </label>
  );
};

// Feb 28, 2026 — generic Profile toggle that syncs to localStorage AND PUTs
// to /api/auth/profile so the preference survives device changes. Subscribes
// to the `writeright:profile-synced` window event so it re-reads localStorage
// after /api/auth/me hydrates on a fresh device. Used for the three
// previously-localStorage-only toggles: skip_sibling_cleanup,
// high_contrast_mode, pe_always_remarkable.
const PersistedToggle = ({
  localKey,
  serverKey,
  testId,
  // If true, the UI checked-state is the INVERSE of what's in localStorage
  // (e.g. Sibling Cleanup: ON in UI = `writeright_skip_sibling_cleanup`
  // === 'false'). Keeps the legacy localStorage semantics intact while
  // letting the UI read naturally.
  invertedLocal = false,
  onToastText = (checked) => (checked ? 'Setting enabled' : 'Setting disabled'),
  onAfter = null,
}) => {
  const readLocal = useCallback(() => {
    const raw = localStorage.getItem(localKey);
    const truthy = raw === 'true';
    return invertedLocal ? !truthy : truthy;
  }, [localKey, invertedLocal]);
  const [on, setOn] = useState(readLocal);
  useEffect(() => {
    const sync = () => setOn(readLocal());
    window.addEventListener('storage', sync);
    window.addEventListener('writeright:profile-synced', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('writeright:profile-synced', sync);
    };
  }, [readLocal]);
  return (
    <Switch
      checked={on}
      onCheckedChange={(checked) => {
        setOn(checked);
        // For inverted toggles, UI ON → localStorage 'false'.
        const storedValue = invertedLocal ? !checked : checked;
        localStorage.setItem(localKey, storedValue ? 'true' : 'false');
        const API = window.__WR_API_BASE__;
        if (API && serverKey) {
          // The server stores the RAW preference (not the inverted UI).
          // For skip_sibling_cleanup this matches the legacy meaning where
          // localStorage = "skip" flag.
          axios.put(
            `${API}/api/auth/profile`,
            { [serverKey]: storedValue },
            { withCredentials: true }
          ).then(() => {
            // Feb 28, 2026 — visible "✓ Synced across devices" badge.
            announceProfileSaved(true);
          }).catch((err) => {
            if (err?.response?.status >= 500) {
              console.warn(`Pref ${serverKey} save failed:`, err.message);
            }
            announceProfileSaved(false);
          });
        }
        if (typeof onAfter === 'function') onAfter(checked);
        toast.success(onToastText(checked), { duration: 1500 });
      }}
      data-testid={testId}
    />
  );
};

// Share Session Modal - Combined Live Session & History
export const ShareModal = ({ shareCode, setShowShareModal, endShareSession, userRole, showShareModal, historyShareCode, startShareHistory, startShareSession }) => {
  const [activeTab, setActiveTab] = useState(shareCode ? 'live' : 'history');
  const [selectedDuration, setSelectedDuration] = useState(72); // Default to 72 hours
  const [selectedDateRange, setSelectedDateRange] = useState(1); // Default to today only
  const [isCreating, setIsCreating] = useState(false);
  
  // Only show if showShareModal is true AND (shareCode OR it was opened intentionally)
  if (!showShareModal) return null;
  
  const isPatient = userRole === 'patient';
  const isProvider = userRole === 'provider';
  
  // Combined share code - use existing share code or create one
  const activeShareCode = shareCode || historyShareCode;
  
  // Handle creating share with selected duration AND date range
  const handleStartShare = async () => {
    setIsCreating(true);
    try {
      await startShareSession(selectedDuration, selectedDateRange);
    } finally {
      setIsCreating(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <Card className="w-full max-w-md bg-card border-border shadow-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-lg">
            <span className="flex items-center gap-2 text-foreground">
              <Share2 className="w-5 h-5 text-primary" />
              {isPatient ? 'Share Your Draft' : 'Share with Medic'}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setShowShareModal(false)} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </CardTitle>
          {isProvider && (
            <p className="text-xs text-muted-foreground mt-1">
              One link gives your medic access to live session AND completed notes
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Simplified Provider Sharing - ONE Link */}
          {isProvider && (
            <div className="space-y-3">
              {activeShareCode ? (
                <>
                  {/* Active Share Status */}
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-sm font-medium text-green-500">Sharing Active</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Your medic can see your live work AND toggle to view completed notes
                    </p>
                  </div>
                  
                  {/* Share Code */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Share Code (for quick access)</Label>
                    <div className="flex items-center gap-2 p-2 bg-secondary rounded-lg">
                      <code className="text-lg font-mono font-bold text-primary tracking-widest flex-1">{activeShareCode}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          copyToClipboard(activeShareCode);
                          toast.success('Code copied!');
                        }}
                        className="h-8"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Share Link */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Share Link (copy & send to medic)</Label>
                    <div className="flex items-center gap-2 p-2 bg-secondary rounded-lg">
                      <code className="text-xs text-muted-foreground truncate flex-1">
                        {window.location.origin}/view/{activeShareCode}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          copyToClipboard(`${window.location.origin}/view/${activeShareCode}`);
                          toast.success('Link copied!');
                        }}
                        className="h-8"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* What medic sees */}
                  <div className="p-2.5 bg-secondary/50 rounded-lg border border-border">
                    <p className="text-xs font-medium text-foreground mb-1.5">Medic View Includes:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        Live typing (real-time)
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                        Today's completed notes
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                        Propose meds/profiles
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
                        Mark orders complete
                      </div>
                    </div>
                  </div>
                  
                  {/* End Session Button */}
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => {
                      if (shareCode) endShareSession();
                      toast.success('Sharing ended');
                    }}
                  >
                    End Sharing Session
                  </Button>
                </>
              ) : (
                <>
                  {/* Duration Selection - How long link stays active */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      Link expires after:
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {SHARE_DURATIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setSelectedDuration(opt.value)}
                          className={`p-2 rounded-lg border text-center transition-all ${
                            selectedDuration === opt.value
                              ? 'border-primary bg-primary/10 ring-1 ring-primary'
                              : 'border-border bg-secondary/50 hover:bg-secondary'
                          }`}
                        >
                          <p className={`text-sm font-medium ${selectedDuration === opt.value ? 'text-primary' : 'text-foreground'}`}>
                            {opt.label}
                          </p>
                          <p className="text-[10px] text-muted-foreground">{opt.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Date Range Selection - How far back history goes */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <CalendarDays className="w-4 h-4 text-blue-500" />
                      Show history from:
                    </Label>
                    <div className="grid grid-cols-4 gap-2">
                      {DATE_RANGE_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setSelectedDateRange(opt.value)}
                          className={`p-2 rounded-lg border text-center transition-all ${
                            selectedDateRange === opt.value
                              ? 'border-blue-500 bg-blue-500/10 ring-1 ring-blue-500'
                              : 'border-border bg-secondary/50 hover:bg-secondary'
                          }`}
                        >
                          <p className={`text-sm font-medium ${selectedDateRange === opt.value ? 'text-blue-500' : 'text-foreground'}`}>
                            {opt.label}
                          </p>
                          <p className="text-[10px] text-muted-foreground">{opt.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Start Sharing */}
                  <div className="text-center py-2">
                    <Button
                      onClick={handleStartShare}
                      disabled={isCreating}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {isCreating ? (
                        <>Creating...</>
                      ) : (
                        <>
                          <Share2 className="w-4 h-4 mr-2" />
                          Share {selectedDateRange === 1 ? "Today's" : `${selectedDateRange} Days`} History
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Quick explanation */}
                  <div className="p-2.5 bg-secondary/50 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground">
                      <strong>One link, full access:</strong> Your medic will see a dashboard with your live work AND {selectedDateRange === 1 ? "today's" : `last ${selectedDateRange} days of`} completed notes. They can propose medications and mark orders as done.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Patient Sharing - Keep simple */}
          {isPatient && shareCode && (
            <div className="space-y-3">
              <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                <Label className="text-xs text-muted-foreground">Preview & Share Link</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-xs text-foreground break-all flex-1">
                    {window.location.origin}/view/{shareCode}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      copyToClipboard(`${window.location.origin}/view/${shareCode}`);
                      toast.success('Link copied!');
                    }}
                    className="h-8"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-green-500" />
                  Preview your draft before downloading
                </p>
                <p className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-green-500" />
                  Share with a friend for feedback
                </p>
              </div>
            </div>
          )}
          
          {isPatient && !shareCode && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-3">
                Create a shareable preview link
              </p>
              <Button onClick={() => startShareSession(24)} className="bg-green-600 hover:bg-green-700">
                <Share2 className="w-4 h-4 mr-2" />
                Create Share Link
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Mood Info Modal
export const MoodInfoModal = ({ show, onClose }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <Card className="w-full max-w-lg bg-card border-border shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-lg">
            <span className="flex items-center gap-2 text-foreground">
              <Heart className="w-5 h-5 text-pink-500" />
              Why Mood Matters
            </span>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground italic">
            "It won't fix your mood. But it will let you see it. And that's half the battle."
          </p>
          
          <div className="space-y-3">
            <p className="text-sm text-foreground">
              These mood themes aren't just about colors. They're about <strong>acknowledging where you are</strong> while doing what you have to do.
            </p>
            
            <div className="grid grid-cols-3 gap-2 py-3">
              {MOOD_THEMES.map(mood => (
                <div key={mood.id} className="text-center p-2 rounded-lg bg-secondary/50">
                  <span className="text-xl">{mood.emoji}</span>
                  <p className="text-xs text-foreground mt-1">{mood.name}</p>
                </div>
              ))}
            </div>
            
            <div className="p-3 bg-secondary rounded-lg space-y-2">
              <p className="text-sm text-foreground font-semibold">You carry it all.</p>
              <p className="text-xs text-muted-foreground">
                You spend all day absorbing everyone else's concerns - every patient, every family, every crisis. By the time you clock out, you've carried it all.
              </p>
            </div>
            
            <div className="p-3 bg-primary/10 rounded-lg border border-primary/30 space-y-2">
              <p className="text-sm text-foreground font-semibold">Naming is the first step.</p>
              <p className="text-xs text-muted-foreground">
                Clicking a mood isn't just changing a color scheme. It's <strong>labeling how you feel</strong> while you're doing what you have to do, even when you don't want to.
              </p>
              <p className="text-xs text-muted-foreground">
                It's a micro-moment of self-awareness in a job that rarely gives you space for it.
              </p>
            </div>
            
            <div className="p-3 bg-secondary rounded-lg space-y-2">
              <p className="text-sm text-foreground font-semibold">The buffer between work and home.</p>
              <p className="text-xs text-muted-foreground">
                Sometimes, naming your mood - even just to yourself, even just by tapping a button - is the first step in decompression. It's the buffer between <em>work you</em> and <em>home you</em>.
              </p>
            </div>
          </div>
          
          <div className="pt-3 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              <Heart className="w-3 h-3 inline mr-1 text-pink-500" />
              Take care of yourself. You can't pour from an empty cup.
            </p>
          </div>
          
          <Button onClick={onClose} className="w-full">
            Got it
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Save Template Modal
export const SaveTemplateModal = ({ 
  show, 
  onClose, 
  newTemplateName, 
  setNewTemplateName, 
  newTemplateDesc, 
  setNewTemplateDesc,
  notes,
  letterType,
  specialties,
  imagingOrders,
  onSave 
}) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <Card className="w-full max-w-md bg-card border-border shadow-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-lg">
            <span className="flex items-center gap-2 text-foreground">
              <span className="text-amber-400">*</span>
              Save as Template
            </span>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Template Name *</Label>
            <input
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="e.g., Knee Pain Referral"
              className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <input
              value={newTemplateDesc}
              onChange={(e) => setNewTemplateDesc(e.target.value)}
              placeholder="Brief description of when to use this"
              className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
            />
          </div>
          <div className="p-3 bg-secondary rounded-lg text-xs text-muted-foreground">
            <p className="font-semibold mb-1">This will save:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Your current notes text</li>
              {letterType && <li>Letter type: {letterType}</li>}
              {specialties?.some(s => s) && <li>Specialties: {specialties.filter(s => s).join(', ')}</li>}
              {imagingOrders?.some(i => i.type) && <li>Imaging orders: {imagingOrders.filter(i => i.type).map(i => `${i.type}${i.bodyPart ? ` (${i.bodyPart})` : ''}`).join(', ')}</li>}
            </ul>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button onClick={onSave} className="flex-1">
              Save Template
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Profile Settings Modal
// Mar 2, 2026 — self-serve Change Password. Local (username/password) users
// only. Google-auth users are told to manage credentials via Google. No
// security-question prompt — the provider explicitly asked for this after
// realizing the seeded accounts never had a real security question configured.
const ChangePasswordSection = ({ user }) => {
  const API_BASE = window.__WR_API_BASE__;
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const isLocal = user?.auth_type === 'local';
  if (!user || !isLocal) return null;

  const canSubmit =
    current.trim().length >= 6 &&
    next.trim().length >= 6 &&
    confirm.trim() === next.trim() &&
    next.trim() !== current.trim();

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE}/api/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      toast.success('Password updated. Other devices signed out.');
      setCurrent(''); setNext(''); setConfirm('');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <details
      className="space-y-2 border border-border/50 rounded-lg p-3 bg-secondary/20"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid="change-password-section"
    >
      <summary className="text-sm flex items-center gap-1.5 cursor-pointer select-none text-muted-foreground hover:text-foreground">
        <KeyRound className="w-3.5 h-3.5" />
        Change password
        <span className="text-[10px] text-muted-foreground/60 ml-auto">no security question needed</span>
      </summary>
      <div className="mt-3 space-y-2">
        <div>
          <Label className="text-xs text-muted-foreground">Current password</Label>
          <div className="relative">
            <Input
              type={showCurrent ? 'text' : 'password'}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="••••••••"
              className="h-9 text-sm pr-9"
              autoComplete="current-password"
              data-testid="change-password-current"
            />
            <button
              type="button"
              onClick={() => setShowCurrent(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
              data-testid="change-password-toggle-current"
            >
              {showCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">New password (min 6 chars)</Label>
          <div className="relative">
            <Input
              type={showNext ? 'text' : 'password'}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="••••••••"
              className="h-9 text-sm pr-9"
              autoComplete="new-password"
              data-testid="change-password-new"
            />
            <button
              type="button"
              onClick={() => setShowNext(v => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              tabIndex={-1}
              data-testid="change-password-toggle-new"
            >
              {showNext ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Confirm new password</Label>
          <Input
            type={showNext ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="••••••••"
            className="h-9 text-sm"
            autoComplete="new-password"
            data-testid="change-password-confirm"
          />
          {confirm && confirm !== next && (
            <p className="text-[11px] text-rose-400 mt-1">Passwords don't match</p>
          )}
          {next && next === current && next.length > 0 && (
            <p className="text-[11px] text-amber-400 mt-1">New password must differ from current</p>
          )}
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            className="h-9 px-4 text-xs font-bold"
            disabled={!canSubmit || saving}
            onClick={submit}
            data-testid="change-password-submit"
          >
            {saving ? 'Updating…' : 'Update password'}
          </Button>
          <span className="text-[10px] text-muted-foreground italic">
            Other signed-in devices will be logged out.
          </span>
        </div>
      </div>
    </details>
  );
};

const BUILTIN_PHRASES = [
  { phrase: '.bhcry', desc: 'BH therapeutic rapport (active listening, reframing, empathy)', auto: 'F32, F41, F43, F90' },
  { phrase: '.keyresults', desc: 'Key Results review template', auto: null },
  { phrase: '.PEadd', desc: 'Full physical exam template (all systems)', auto: null },
  { phrase: '.dm2', desc: 'Diabetes management (A1c, meds, goals)', auto: null },
  { phrase: '.htn', desc: 'Hypertension management (BP, meds, goals)', auto: null },
  { phrase: '.rtd', desc: 'Return to duty — no restrictions', auto: null },
  { phrase: '.qtrs', desc: 'Return to quarters (24/48/72h)', auto: null },
  { phrase: '.tbiPRA', desc: 'TBI PRA Protocol (6 stages + MEDPROS template)', auto: null },
  { phrase: '.tbiNSI', desc: 'NSI 22 questions + scoring + patient watch list', auto: null },
  { phrase: '.cacs', desc: 'Coronary calcium score referral', auto: null },
  { phrase: '.educac', desc: 'CAC scan patient education', auto: null },
  { phrase: '.eduMESA', desc: 'MESA coronary age score', auto: null },
  { phrase: '.eduhld', desc: 'HDL improvement education', auto: null },
  { phrase: '.lowdoseCT', desc: 'USPSTF low-dose lung CT', auto: null },
  { phrase: '.colonoscopy', desc: 'USPSTF colorectal screening', auto: null },
  { phrase: '.mammo', desc: 'USPSTF mammography', auto: null },
  { phrase: '.pap', desc: 'USPSTF cervical screening', auto: null },
  { phrase: '.psa', desc: 'USPSTF prostate screening', auto: null },
  { phrase: '.aaa', desc: 'USPSTF AAA screening', auto: null },
];

const DotPhraseLibrarySection = () => {
  const API_BASE = window.__WR_API_BASE__;
  const [customPhrases, setCustomPhrases] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');
  const [newContent, setNewContent] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadCustom = useCallback(() => {
    axios.get(`${API_BASE}/api/dot-phrases`, { withCredentials: true })
      .then(res => setCustomPhrases(res.data.phrases || []))
      .catch(() => {});
  }, [API_BASE]);

  useEffect(() => { loadCustom(); }, [loadCustom]);

  const handleAdd = async () => {
    if (!newKeyword.trim() || !newContent.trim()) { toast.error('Keyword and content required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/api/dot-phrases`, { keyword: newKeyword.trim(), content: newContent.trim() }, { withCredentials: true });
      toast.success(`Added ${newKeyword.startsWith('.') ? newKeyword : '.' + newKeyword}`);
      setNewKeyword(''); setNewContent(''); setShowAddForm(false);
      loadCustom();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, keyword) => {
    if (!window.confirm(`Delete ${keyword}?`)) return;
    try {
      await axios.delete(`${API_BASE}/api/dot-phrases/${id}`, { withCredentials: true });
      toast.success(`Deleted ${keyword}`);
      loadCustom();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm flex items-center gap-2">
        Dot Phrase Library
        <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Quick Reference</span>
      </Label>
      <p className="text-xs text-muted-foreground">
        Type in notes + space to expand. Add your own custom phrases below.
      </p>
      <div className="space-y-1 p-3 bg-secondary rounded-lg max-h-[350px] overflow-y-auto">
        {/* Custom phrases first */}
        {customPhrases.length > 0 && (
          <>
            <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-1">Your Custom Phrases</p>
            {customPhrases.map(dp => (
              <div key={dp.id} className="flex items-start gap-2 py-1.5 border-b border-emerald-500/20 last:border-0">
                <code className="text-xs font-bold text-emerald-400 shrink-0 min-w-[100px]">{dp.keyword}</code>
                <span className="text-xs text-muted-foreground flex-1 line-clamp-2">{dp.content.substring(0, 80)}{dp.content.length > 80 ? '...' : ''}</span>
                <button onClick={() => handleDelete(dp.id, dp.keyword)} className="shrink-0 p-0.5 text-muted-foreground hover:text-destructive" title="Delete">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            <div className="border-b border-border/50 my-2" />
          </>
        )}
        {/* Built-in phrases */}
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Built-in Phrases</p>
        {BUILTIN_PHRASES.map(dp => (
          <div key={dp.phrase} className="flex items-start gap-2 py-1 border-b border-border/30 last:border-0">
            <code className="text-xs font-bold text-primary shrink-0 min-w-[100px]">{dp.phrase}</code>
            <span className="text-xs text-muted-foreground flex-1">{dp.desc}</span>
            {dp.auto && (
              <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded shrink-0">{dp.auto}</span>
            )}
          </div>
        ))}
        <p className="text-[10px] text-muted-foreground/60 mt-2 italic">
          Auto-triggered: J30 (Allergies), J01/J06 (Sinusitis), K21 (GERD), L50 (Urticaria), F-codes (BH)
        </p>
      </div>
      {/* Add new phrase */}
      {!showAddForm ? (
        <Button variant="outline" size="sm" className="h-7 text-xs w-full border-dashed border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" onClick={() => setShowAddForm(true)}>
          <Plus className="w-3 h-3 mr-1" /> Add Custom Dot Phrase
        </Button>
      ) : (
        <div className="p-3 bg-secondary rounded-lg border border-emerald-500/30 space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-xs shrink-0 w-16">Keyword</Label>
            <Input value={newKeyword} onChange={(e) => setNewKeyword(e.target.value)} placeholder=".myPhrase" className="h-7 text-xs bg-input" />
          </div>
          <div>
            <Label className="text-xs">Content (what it expands to)</Label>
            <textarea value={newContent} onChange={(e) => setNewContent(e.target.value)} placeholder="The text that replaces the keyword when you type it + space..." className="w-full mt-1 bg-input border border-border rounded-lg p-2 text-xs text-foreground min-h-[60px] resize-none outline-none focus:ring-1 focus:ring-emerald-500/50" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setShowAddForm(false); setNewKeyword(''); setNewContent(''); }}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={handleAdd} disabled={saving}>
              {saving ? 'Saving...' : 'Save Phrase'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ProfileModal = ({ 
  show, 
  user, 
  onClose, 
  profileSignature, 
  setProfileSignature,
  profileMTF, 
  setProfileMTF,
  profileRank, 
  setProfileRank,
  defaultMood,
  setDefaultMood,
  defaultTextSize,
  setDefaultTextSize,
  defaultIncludeROS,
  setDefaultIncludeROS,
  defaultIncludePE,
  setDefaultIncludePE,
  defaultMedicMode,
  setDefaultMedicMode,
  defaultVisitType,
  setDefaultVisitType,
  visitSchedule,
  setVisitSchedule,
  defaultInputMode,
  setDefaultInputMode,
  // Personal info for letter auto-fill
  personalFirstName,
  setPersonalFirstName,
  personalLastName,
  setPersonalLastName,
  personalDOB,
  setPersonalDOB,
  personalPhone,
  setPersonalPhone,
  personalOccupation,
  setPersonalOccupation,
  // v6.8.44 — Saved Medical History / Injuries
  savedInjuries,
  setSavedInjuries,
  providerLetterTone,
  setProviderLetterTone,
  letterheadLine1,
  setLetterheadLine1,
  letterheadLine2,
  setLetterheadLine2,
  letterheadAddress,
  setLetterheadAddress,
  userRole,
  clinicalTone,
  setClinicalTone,
  onSave,
  isSaving
}) => {
  // Feb 28, 2026 — fix Switch visual desync after persistProfilePref
  // writes localStorage but doesn't change parent state. We force a
  // re-render via a tick counter so the Switch's `checked` prop is
  // re-evaluated from localStorage on every toggle.
  const [, setSettingsTick] = useState(0);
  const bumpTick = () => setSettingsTick((t) => t + 1);

  // Feb 28, 2026 — listen for server hydration so toggles reflect
  // server-persisted values on first paint (e.g. user logs in on a
  // new device and the modal is already open).
  useEffect(() => {
    const handler = () => bumpTick();
    window.addEventListener('writeright:profile-synced', handler);
    return () => window.removeEventListener('writeright:profile-synced', handler);
  }, []);

  // Feb 28, 2026 — close on Escape + click-outside (previously the
  // modal trapped the user until they found the X button).
  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, onClose]);

  if (!show) return null;
  
  const ranks = ['PVT','PV2','PFC','SPC','CPL','SGT','SSG','SFC','MSG','1SG','SGM','CSM',
    '2LT','1LT','CPT','MAJ','LTC','COL','BG','MG','LTG','GEN'];
  
  const textSizeOptions = [
    { value: 0, label: 'Regular (100%)', description: 'Default size' },
    { value: 1, label: 'Medium (150%)', description: 'Easier to read' },
    { value: 2, label: 'Large (200%)', description: 'Maximum readability' }
  ];
  
  const visitTypeOptions = [
    { value: 'in_person_exam', label: 'In-Person (with exam)', description: 'Standard office visit' },
    { value: 'in_person_counseling', label: 'In-Person (counseling)', description: 'Counseling/discussion only' },
    { value: 'virtual', label: 'Virtual/Telehealth', description: 'Video or phone appointment' }
  ];
  
  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 pt-16 sm:pt-4 overflow-y-auto"
      style={{ zIndex: 9999 }}
      onMouseDown={(e) => {
        // Feb 28, 2026 — close on backdrop click. We check that the
        // pointer-down event hit the backdrop itself (not bubbled from
        // a nested element) so users can drag-select text inside the
        // modal without it dismissing.
        if (e.target === e.currentTarget && onClose) onClose();
      }}
      data-testid="profile-settings-backdrop"
    >
      <Card
        className="w-full max-w-lg bg-card border-border shadow-2xl max-h-[85vh] overflow-y-auto my-auto"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="profile-settings-modal"
      >
        <CardHeader className="pb-3 sticky top-0 bg-card z-10 border-b border-border">
          <CardTitle className="flex items-center justify-between text-lg">
            <span className="flex items-center gap-2 text-foreground">
              Profile Settings
            </span>
            <div className="flex items-center gap-2">
              {/* Mar 1, 2026 — Duplicate Save button at the TOP. Provider
                  complained the bottom Save was "too far down" to reach
                  after editing one field — now the sticky header surfaces
                  the same action without scrolling. */}
              <Button
                onClick={onSave}
                disabled={isSaving}
                size="sm"
                className="h-8 px-3 text-xs font-bold"
                data-testid="save-profile-btn-top"
              >
                {isSaving ? 'Saving...' : 'Save Profile'}
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-secondary rounded-lg">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-12 h-12 rounded-full" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-primary text-lg font-bold">{(user?.name || user?.username || 'G')?.charAt(0).toUpperCase()}</span>
                </div>
              )}
              <div>
                <p className="font-semibold text-foreground">{user?.name || user?.username || 'Guest'}</p>
                <p className="text-xs text-muted-foreground">{user?.email || 'Guest Mode'}</p>
              </div>
            </div>
          </div>

          {/* Mar 2, 2026 — Self-serve Change Password (local users only) */}
          <ChangePasswordSection user={user} />
          
          {/* Account Merge — link Google OAuth with password login.
              Collapsed by default (one-time use, rarely needed) so it stops
              feeling like a nag on every Settings open. Backend auto-migrates
              guest data on login already; this manual merge is ONLY for linking
              two real accounts (e.g., Google + password signup). */}
          {userRole === 'provider' && (
          <details className="space-y-2 border border-border/50 rounded-lg p-3 bg-secondary/20" data-testid="account-merge-section">
            <summary className="text-sm flex items-center gap-1.5 cursor-pointer select-none text-muted-foreground hover:text-foreground">
              <ArrowRightLeft className="w-3.5 h-3.5" />
              Link a duplicate account (Google + password)
              <span className="text-[10px] text-muted-foreground/60 ml-auto">optional · one-time</span>
            </summary>
            <p className="text-xs text-muted-foreground mt-2">
              Only needed if you signed up with BOTH Google and a password at different times. Guest-to-login migration happens automatically on login — you do NOT need to touch this.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input 
                placeholder="Other account username or email"
                className="h-11 text-sm flex-1" 
                data-testid="merge-account-input"
                id="merge-account-input"
              />
              <Button 
                variant="outline" size="sm" 
                className="h-11 px-4 text-sm gap-1.5 sm:w-auto w-full"
                data-testid="merge-account-btn"
                onClick={async () => {
                  const input = document.getElementById('merge-account-input')?.value?.trim();
                  if (!input) return;
                  const API = window.__WR_API_BASE__;
                  try {
                    // Look up the other user
                    const lookupRes = await fetch(`${API}/api/auth/lookup?q=${encodeURIComponent(input)}`);
                    const lookupData = await lookupRes.json();
                    if (!lookupData?.user_id) {
                      alert('Account not found. Check the username or email.');
                      return;
                    }
                    const confirm = window.confirm(
                      `Merge "${lookupData.name || lookupData.username}" (${lookupData.user_id}) into your current account (${user.user_id || user.id})?\n\nAll history, stats, and team notes will be combined.`
                    );
                    if (!confirm) return;
                    const mergeRes = await fetch(`${API}/api/auth/merge-accounts`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ 
                        from_user_id: lookupData.user_id, 
                        to_user_id: user.user_id || user.id 
                      })
                    });
                    const mergeData = await mergeRes.json();
                    if (mergeData.merged) {
                      alert(`Merged! ${mergeData.history_migrated} notes + ${mergeData.perf_migrated} perf logs transferred.`);
                      document.getElementById('merge-account-input').value = '';
                    } else {
                      alert('Merge failed: ' + (mergeData.detail || 'Unknown error'));
                    }
                  } catch (err) {
                    alert('Error: ' + err.message);
                  }
                }}
              >
                <ArrowRightLeft className="w-3 h-3" />
                Merge
              </Button>
            </div>
          </details>
          )}
          
          {/* ===== PROVIDER PREFERENCES (TOP) ===== */}

          {/* Feb 28, 2026 (v6.8.40) — Re-prioritized UI/Theme block.
              User feedback: "toggles and moods should be higher" — these
              are the most frequently changed prefs (per-shift / per-mood)
              so they live at the very top of the Settings panel now. */}
          <div className="space-y-3 p-3 rounded-lg border border-primary/20 bg-primary/[0.04]" data-testid="display-themes-block">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold tracking-wide uppercase text-primary/90">Display & Theme</Label>
              <span className="text-[9px] px-2 py-0.5 bg-primary/15 text-primary rounded font-bold">QUICK ACCESS</span>
            </div>

            {/* Default Mood Theme */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Default Mood Theme</Label>
              <select
                value={defaultMood || ''}
                onChange={(e) => setDefaultMood(e.target.value)}
                className="w-full h-11 px-3 rounded-md border border-border bg-input text-sm"
                style={{ fontSize: '15px' }}
                data-testid="profile-mood-theme"
              >
                <option value="" style={{ padding: '12px' }}>Use last selected mood</option>
                {MOOD_THEMES.map(mood => (
                  <option key={mood.id} value={mood.id} style={{ padding: '12px', fontSize: '15px' }}>
                    {mood.emoji} {mood.name} - {mood.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Minimalist View */}
            <label
              className="flex items-start gap-3 cursor-pointer rounded-lg border border-border bg-secondary/40 p-2.5"
              data-testid="profile-minimalist-view-row"
            >
              <input
                type="checkbox"
                checked={localStorage.getItem('writeright_minimalist_tiles') === '1'}
                onChange={(e) => {
                  const next = e.target.checked ? '1' : '0';
                  try { localStorage.setItem('writeright_minimalist_tiles', next); } catch { /* ignore */ }
                  document.documentElement.setAttribute('data-minimalist', next);
                  persistProfilePref({ minimalist_tiles: e.target.checked });
                  try { window.dispatchEvent(new CustomEvent('writeright:minimalist-changed')); } catch { /* ignore */ }
                  bumpTick();
                }}
                className="w-4 h-4 rounded border-border mt-0.5"
                data-testid="profile-minimalist-tiles-checkbox"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm flex items-center gap-2">
                  Minimalist View
                  <span className="text-[10px] px-2 py-0.5 bg-violet-500/20 text-violet-300 rounded font-bold">CALM-MODE</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                  Softens cards, drops gradient chrome, calms animations, and collapses info banners. Generate, Next Patient, and conflict alerts stay full-color.
                </p>
              </div>
            </label>

            {/* Demo Mode — provider only */}
            {userRole === 'provider' && (
              <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-border bg-secondary/40 p-2.5" data-testid="profile-demo-mode-row">
                <input
                  type="checkbox"
                  checked={localStorage.getItem('writeright_demo_mode') === '1'}
                  onChange={(e) => {
                    const next = e.target.checked ? '1' : '0';
                    try { localStorage.setItem('writeright_demo_mode', next); } catch { /* ignore */ }
                    persistProfilePref({ demo_mode: e.target.checked });
                    window.dispatchEvent(new CustomEvent('writeright:demo-mode-toggle', { detail: { enabled: e.target.checked } }));
                    bumpTick();
                  }}
                  className="w-4 h-4 rounded border-border mt-0.5"
                  data-testid="profile-demo-mode-checkbox"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm flex items-center gap-2">
                    Demo Mode
                    <span className="text-[10px] px-2 py-0.5 bg-fuchsia-500/20 text-fuchsia-300 rounded font-bold">PITCH-READY</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Adds a &quot;Seed Demo Patient&quot; button on the input card — for pitches / training videos.
                  </p>
                </div>
              </label>
            )}

            {/* High Contrast Mode */}
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/40 px-3 py-2" data-testid="high-contrast-row">
              <div className="flex-1 min-w-0">
                <Label className="text-sm flex items-center gap-2">
                  High Contrast Mode
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded font-bold">A11Y</span>
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Forces accents (buttons, toggles, focus rings, generate button) to slate/emerald regardless of mood. Helpful on glare-prone monitors.
                </p>
              </div>
              <PersistedToggle
                localKey="writeright_high_contrast"
                serverKey="high_contrast_mode"
                testId="high-contrast-toggle"
                onToastText={(checked) => (checked ? 'High Contrast Mode ON' : 'High Contrast Mode OFF')}
                onAfter={(checked) => {
                  try {
                    document.documentElement.classList.toggle('high-contrast', checked);
                    document.body.classList.toggle('high-contrast', checked);
                  } catch { /* ignore */ }
                }}
              />
            </div>
          </div>

          {/* Default Text Size Setting */}
          <div className="space-y-2">
            <Label className="text-sm">Default Text Size</Label>
            <p className="text-xs text-muted-foreground">
              Choose your preferred text size for better readability
            </p>
            <select 
              value={defaultTextSize} 
              onChange={(e) => setDefaultTextSize(parseInt(e.target.value))}
              className="w-full h-12 px-3 rounded-md border border-border bg-input text-base"
              style={{ fontSize: '16px' }}
              data-testid="profile-text-size"
            >
              {textSizeOptions.map(opt => (
                <option key={opt.value} value={opt.value} style={{ padding: '12px', fontSize: '16px' }}>
                  {opt.label} - {opt.description}
                </option>
              ))}
            </select>
          </div>
          
          {/* Default Note Sections — Provider Only */}
          {userRole === 'provider' && (
          <div className="space-y-2">
            <Label className="text-sm">Default Note Sections</Label>
            <p className="text-xs text-muted-foreground">
              Choose which sections to include in clinical notes by default
            </p>
            <div className="space-y-2 p-3 bg-secondary rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={defaultIncludeROS}
                  onChange={(e) => setDefaultIncludeROS(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                  data-testid="profile-ros-checkbox"
                />
                <span className="text-sm">Include ROS (Review of Systems)</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={defaultIncludePE}
                  onChange={(e) => setDefaultIncludePE(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                  data-testid="profile-pe-checkbox"
                />
                <span className="text-sm">Include PE (Physical Exam)</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!defaultMedicMode}
                  onChange={(e) => setDefaultMedicMode && setDefaultMedicMode(e.target.checked)}
                  className="w-4 h-4 rounded border-border"
                  data-testid="profile-medic-mode-checkbox"
                />
                <span className="text-sm">Medic Mode by default <span className="text-muted-foreground text-xs italic">(HPI + ROS only — skip PE/AP)</span></span>
              </label>
              {/* Minimalist View + Demo Mode moved to the new "Display &
                  Theme" block at the top of Settings (Feb 28, 2026 v6.8.40). */}
              <p className="text-xs text-muted-foreground mt-2">
                You can still toggle these per-note when creating documents
              </p>
            </div>
          </div>
          )}

          {/* Dot Phrase Library + Custom Phrases — Provider Only */}
          {userRole === 'provider' && <DotPhraseLibrarySection />}

          {/* White Space Toggle — Provider Only */}
          {userRole === 'provider' && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border bg-secondary/30">
              <div className="flex-1 min-w-0">
                <Label className="text-sm flex items-center gap-2">
                  White Space Reminders
                  <span className="text-[10px] px-2 py-0.5 bg-sky-500/20 text-sky-400 rounded font-bold">NON-NAG</span>
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Gentle "Ready to submit?" banner during your white space gaps. Fires after 3 patient switches with ≥ 2 unsubmitted notes from today and 45 min since last submit. Max 1 per hour.
                </p>
              </div>
              <Switch
                checked={localStorage.getItem('writeright_whitespace_disabled') !== 'true'}
                onCheckedChange={(checked) => {
                  localStorage.setItem('writeright_whitespace_disabled', checked ? 'false' : 'true');
                  // Server stores the RAW DISABLED flag — checked=true means
                  // reminders ENABLED, so whitespace_disabled=false.
                  persistProfilePref({ whitespace_disabled: !checked });
                  bumpTick();
                  toast.success(checked ? 'White Space reminders ON' : 'White Space reminders OFF', { duration: 1500 });
                }}
                data-testid="white-space-toggle"
              />
            </div>
          )}

          {/* Preview the White Space encouragement banner on demand */}
          {userRole === 'provider' && localStorage.getItem('writeright_whitespace_disabled') !== 'true' && (
            <div className="flex items-center justify-between gap-3 pl-7 pr-3 py-1.5">
              <p className="text-[11px] text-muted-foreground italic">
                Want to see what the encouragement banner looks like?
              </p>
              <button
                type="button"
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('writeright:force-whitespace-preview'));
                    toast.success('Showing encouragement banner preview', { duration: 1500 });
                  } catch (e) { /* ignore */ }
                }}
                className="text-[11px] font-semibold text-amber-400 hover:text-amber-300 underline underline-offset-2"
                data-testid="whitespace-preview-btn"
              >
                Preview it now
              </button>
            </div>
          )}

          {/* Skip Role Selector Toggle — Provider Only */}
          {userRole === 'provider' && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border bg-secondary/30" data-testid="skip-role-selector-row">
              <div className="flex-1 min-w-0">
                <Label className="text-sm flex items-center gap-2">
                  Skip Role Selector on Login
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded font-bold">FAST-LAUNCH</span>
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Auto-resume Provider mode on every login. Great for daily clinical use. Turn off before demos so attendees see the Role Selector pitch page.
                </p>
              </div>
              <Switch
                checked={localStorage.getItem('writeright_skip_role_selector') === 'true'}
                onCheckedChange={(checked) => {
                  localStorage.setItem('writeright_skip_role_selector', checked ? 'true' : 'false');
                  persistProfilePref({ skip_role_selector: checked });
                  bumpTick();
                  toast.success(checked ? 'Role selector will be skipped on login' : 'Role selector will show on login', { duration: 1500 });
                }}
                data-testid="skip-role-selector-toggle"
              />
            </div>
          )}

          {/* Sibling Cleanup Prompt — Provider Only */}
          {userRole === 'provider' && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border bg-secondary/30" data-testid="sibling-cleanup-row">
              <div className="flex-1 min-w-0">
                <Label className="text-sm flex items-center gap-2">
                  Sibling Cleanup Prompt
                  <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold">DEDUPE</span>
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  After marking a note as Submitted to Genesis, offer to delete sibling draft versions of the same encounter (same age + gender + date). Turn off if you prefer to manage drafts manually.
                </p>
              </div>
              <PersistedToggle
                localKey="writeright_skip_sibling_cleanup"
                serverKey="skip_sibling_cleanup"
                testId="sibling-cleanup-toggle"
                invertedLocal={true}
                onToastText={(checked) => (checked ? 'Sibling cleanup prompt ON' : 'Sibling cleanup prompt OFF')}
              />
            </div>
          )}

          {/* Genesis Dot-Phrase Tokens — Provider Only */}
          {userRole === 'provider' && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border bg-secondary/30" data-testid="genesis-dot-phrases-row">
              <div className="flex-1 min-w-0">
                <Label className="text-sm flex items-center gap-2">
                  Use Genesis Dot-Phrase Tokens
                  <span className="text-[10px] px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded font-bold">.NAME / .FIRST</span>
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Your A/P Summary will begin with the literal token <code className="px-1 py-0.5 bg-secondary rounded text-[10px]">.name</code> instead of the word "Patient". MHS Genesis expands <code className="px-1 py-0.5 bg-secondary rounded text-[10px]">.name</code> to the patient's full name and <code className="px-1 py-0.5 bg-secondary rounded text-[10px]">.first</code> to their first name on paste. Leave OFF if your EHR doesn't support these tokens.
                </p>
              </div>
              {(() => {
                // Feb 28, 2026 — controlled state + server persistence so the
                // toggle survives login on new devices. Previously this was
                // localStorage-only which is browser-local, so the user had
                // to re-toggle on every login. Server value is source of
                // truth (synced server→localStorage in useProfile.js).
                return null;
              })()}
              <GenesisDotPhrasesToggle />
            </div>
          )}

          {/* ICD-10 Chip Personal Usage Reset — Provider Only */}
          {userRole === 'provider' && (() => {
            const usage = (() => {
              try { return JSON.parse(localStorage.getItem('writeright_icd10_usage_v1') || '{}'); }
              catch { return {}; }
            })();
            const trackedCount = Object.keys(usage).length;
            const totalTaps = Object.values(usage).reduce((sum, n) => sum + (Number(n) || 0), 0);
            return (
              <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border bg-secondary/30" data-testid="icd10-usage-row">
                <div className="flex-1 min-w-0">
                  <Label className="text-sm flex items-center gap-2">
                    ICD-10 Chip Personalization
                    {trackedCount > 0 && (
                      <span className="text-[10px] px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded font-bold" data-testid="icd10-usage-count">
                        {trackedCount} CODE{trackedCount === 1 ? '' : 'S'} · {totalTaps} TAP{totalTaps === 1 ? '' : 'S'}
                      </span>
                    )}
                  </Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {trackedCount > 0
                      ? 'The Common ICD-10 chip row re-ranks itself based on your tap history (⭐ marks your top-used chip per topic). Reset to start fresh — useful when your practice mix changes (e.g., clinic ↔ deployment medicine).'
                      : 'Tap any chip in the Common ICD-10 row to start personalizing the order. Once you have 3+ taps on a code, ⭐ appears and chips re-rank by your usage.'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={trackedCount === 0}
                  onClick={() => {
                    try {
                      localStorage.removeItem('writeright_icd10_usage_v1');
                      toast.success('ICD-10 chip ranking reset', { duration: 1500 });
                      // Force component re-render by toggling a dummy state via window event
                      try { window.dispatchEvent(new CustomEvent('writeright:icd10-usage-reset')); } catch { /* ignore */ }
                      // Feb 28, 2026 — also flush to server (REPLACE
                      // semantics) so the reset propagates to every other
                      // device the user logs into.
                      try { window.dispatchEvent(new CustomEvent('writeright:icd10-usage-flush')); } catch { /* ignore */ }
                    } catch (e) {
                      toast.error('Could not reset — try clearing browser storage');
                    }
                  }}
                  className="h-8 text-xs gap-1.5 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-40 flex-shrink-0"
                  data-testid="icd10-usage-reset-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Reset
                </Button>
              </div>
            );
          })()}

          {/* Fixes I Always Make — provider-only.
              Feb 28, 2026. Surfaces detected word-substitutions from the
              user's last 30 days of edited notes; approve to honor on every
              future Generate. */}
          {userRole === 'provider' && <MyCorrectionsSection />}

          {/* High Contrast Mode moved to the top "Display & Theme" block
              (Feb 28, 2026 v6.8.40). */}

          {/* Schedule Override: Always Remarkable PE — Provider Only */}
          {userRole === 'provider' && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border bg-secondary/30" data-testid="pe-override-row">
              <div className="flex-1 min-w-0">
                <Label className="text-sm flex items-center gap-2">
                  Override Schedule — Always Remarkable PE
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded font-bold">NON-STANDARD ROTATIONS</span>
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Ignore your AM/PM schedule and ALWAYS default to PE = on + Remarkable + auto-fill Findings from dictation. Great for providers with mixed rotations, after-hours coverage, or non-template clinics where every visit is hands-on.
                </p>
              </div>
              <PersistedToggle
                localKey="writeright_pe_always_remarkable"
                serverKey="pe_always_remarkable"
                testId="pe-override-toggle"
                onToastText={(checked) => (checked ? 'PE will always default to Remarkable + auto-Findings' : 'Schedule-aware PE defaults restored')}
              />
            </div>
          )}

          {/* Auto-Stream to Default Team — Provider Only */}
          {userRole === 'provider' && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-border bg-secondary/30" data-testid="auto-stream-row">
              <div className="flex-1 min-w-0">
                <Label className="text-sm flex items-center gap-2">
                  Auto-Stream Notes to Default Team
                  <span className="text-[10px] px-2 py-0.5 bg-sky-500/20 text-sky-400 rounded font-bold">RELAY RACE</span>
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Silently shares each generated note with your default team, no link or code shown. (This does NOT create a temp share link — that's a different feature.) Uses the team you selected in the Team panel.
                </p>
                {(() => {
                  // Feb 22, 2026 — fall back to `writeright_active_team`
                  // (set by the Team panel) when the dedicated auto-share
                  // team key isn't set. Previously these were independent
                  // and the toggle showed "No team selected" every login.
                  const teamId = localStorage.getItem('writeright_auto_share_team')
                    || localStorage.getItem('writeright_active_team');
                  const teamName = localStorage.getItem('writeright_active_team_name');
                  return teamId ? (
                    <p className="text-[10px] text-sky-400/80 mt-0.5 font-mono">
                      Streaming to: {teamName || teamId.slice(0, 8) + '…'}
                    </p>
                  ) : (
                    <p className="text-[10px] text-amber-400/70 mt-0.5">
                      No team selected — open Team panel to pick your default.
                    </p>
                  );
                })()}
              </div>
              {(() => {
                // Feb 28, 2026 — controlled state extracted to AutoStreamToggle
                // component (top of file) so React hooks live at component
                // top-level. The toggle re-syncs from a writeright:profile-synced
                // window event so the UI updates after /api/auth/me hydrates.
                return null;
              })()}
              <AutoStreamToggle />
            </div>
          )}
          
          {/* Visit Type Schedule — Provider Only */}
          {userRole === 'provider' && (
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              Visit Type Schedule
              <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">Auto-Defaults</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Set your weekly schedule — visit type auto-defaults each appointment. You can always override per-patient.
            </p>
            <div className="space-y-2 p-3 bg-secondary rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={visitSchedule?.enabled ?? true}
                  onChange={(e) => setVisitSchedule(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="w-4 h-4 rounded border-border"
                  data-testid="schedule-enabled"
                />
                <span className="text-sm">Enable auto-schedule</span>
              </label>
              {visitSchedule?.enabled && (
                <div className="space-y-1">
                  <div className="grid grid-cols-[80px_1fr_1fr] gap-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-1">
                    <span></span><span className="text-center">Morning</span><span className="text-center">Afternoon</span>
                  </div>
                  {['monday','tuesday','wednesday','thursday','friday'].map(day => (
                    <div key={day} className="grid grid-cols-[80px_1fr_1fr] gap-1 items-center" data-testid={`schedule-row-${day}`}>
                      <span className="text-xs font-medium text-foreground capitalize">{day.slice(0,3)}</span>
                      {['am','pm'].map(period => {
                        const val = visitSchedule?.days?.[day]?.[period] || 'in_person_exam';
                        const isVirtual = val === 'virtual' || val === 'results_review_virtual';
                        return (
                          <button
                            key={period}
                            onClick={() => {
                              setVisitSchedule(prev => ({
                                ...prev,
                                days: {
                                  ...prev.days,
                                  [day]: { ...prev.days?.[day], [period]: isVirtual ? 'in_person_exam' : 'virtual' }
                                }
                              }));
                            }}
                            className={`px-2 py-1.5 rounded text-[10px] font-semibold transition-all border ${
                              isVirtual
                                ? 'bg-blue-500/20 border-blue-500/40 text-blue-400'
                                : 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                            }`}
                            data-testid={`schedule-${day}-${period}`}
                          >
                            {isVirtual ? 'Virtual' : 'In-Person'}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                  <p className="text-[10px] text-muted-foreground mt-2 italic">
                    Click to toggle. Applies on Next Pt and app load.
                  </p>
                </div>
              )}
            </div>
          </div>
          )}
          
          {/* Clinical Tone — Provider Only */}
          {userRole === 'provider' && setClinicalTone && (
            <div className="space-y-2">
              <Label className="text-foreground font-medium flex items-center gap-2">
                Clinical Tone
                <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Preference</span>
              </Label>
              <p className="text-xs text-muted-foreground">
                Controls how the AI writes your notes. Empathetic preserves emotional content; Educational emphasizes MOA and patient teaching.
              </p>
              <select
                value={clinicalTone || 'empathetic'}
                onChange={(e) => { setClinicalTone(e.target.value); localStorage.setItem('writeright_clinical_tone', e.target.value); persistProfilePref({ clinical_tone: e.target.value }); }}
                className="w-full h-12 px-3 rounded-md border border-border bg-input text-base"
                style={{ fontSize: '16px' }}
                data-testid="profile-clinical-tone"
              >
                <option value="empathetic">Empathetic — Preserves emotions, therapeutic rapport</option>
                <option value="educational">Educational — MOA of meds, off-label use, patient teaching</option>
                <option value="detailed">Detailed — Maximum clinical detail, nothing abbreviated</option>
                <option value="neutral">Neutral — Standard objective documentation</option>
                <option value="concern">Concern — Red flags, safety screening, escalation</option>
              </select>
            </div>
          )}

          {/* PE Unremarkable — CV Language pref (Mar 3, 2026) */}
          {userRole === 'provider' && (
            <div className="space-y-2" data-testid="profile-pe-cv-language-section">
              <Label className="text-foreground font-medium flex items-center gap-2">
                PE Unremarkable — CV Line
                <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Preference</span>
              </Label>
              <p className="text-xs text-muted-foreground">
                Controls the CV descriptor when PE is Unremarkable. &quot;Hemodynamically stable&quot; is observational only (safer for virtual visits, no auscultation claim). &quot;RRR, no murmurs&quot; is the traditional phrasing for MTFs whose local convention keeps it. Normal in-person exams with dictated findings are unaffected.
              </p>
              <select
                value={(() => { try { return localStorage.getItem('writeright_pe_cv_language') || 'hemodynamically_stable'; } catch { return 'hemodynamically_stable'; } })()}
                onChange={(e) => {
                  const v = e.target.value;
                  try { localStorage.setItem('writeright_pe_cv_language', v); } catch { /* ignore quota */ }
                  persistProfilePref({ pe_cv_language: v });
                  bumpTick();
                }}
                className="w-full h-12 px-3 rounded-md border border-border bg-input text-base"
                style={{ fontSize: '16px' }}
                data-testid="profile-pe-cv-language"
              >
                <option value="hemodynamically_stable">Hemodynamically stable — Observational (default)</option>
                <option value="rrr_no_murmurs">RRR, no murmurs — Traditional (MTF preference)</option>
              </select>
            </div>
          )}

          {/* Patient: NSI Symptom Self-Check */}
          {userRole === 'patient' && (
            <NSISymptomTracker defaultOpen variant="panel" testIdPrefix="settings-nsi" />
          )}

          {/* Default Input Mode */}
          <div className="space-y-2">
            <Label className="text-foreground font-medium flex items-center gap-2">
              Default Input Mode
              <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Preference</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Choose whether to start with Voice or Type tab when opening the app
            </p>
            <select 
              value={defaultInputMode || 'voice'} 
              onChange={(e) => setDefaultInputMode(e.target.value)}
              className="w-full h-12 px-3 rounded-md border border-border bg-input text-base"
              style={{ fontSize: '16px' }}
              data-testid="profile-input-mode"
            >
              <option value="voice" style={{ padding: '12px', fontSize: '16px' }}>Voice - Start with microphone ready</option>
              <option value="text" style={{ padding: '12px', fontSize: '16px' }}>Type - Start with text input</option>
            </select>
          </div>
          
          {/* View Mode - Mobile/Desktop */}
          <div className="space-y-2">
            <Label className="text-foreground font-medium flex items-center gap-2">
              View Mode
              <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">Display</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Select your device type. Desktop forces a wider layout on phones/tablets — useful when auto-detection doesn't work
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  localStorage.setItem('writeright_desktop_view', 'false');
                  persistProfilePref({ force_desktop_view: false });
                  const viewport = document.querySelector('meta[name="viewport"]');
                  if (viewport) viewport.setAttribute('content', 'width=device-width, initial-scale=1');
                  document.body.classList.remove('force-desktop-view');
                  window.dispatchEvent(new CustomEvent('viewmode-changed', { detail: false }));
                  window.dispatchEvent(new Event('resize'));
                }}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  localStorage.getItem('writeright_desktop_view') !== 'true'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/30'
                }`}
                data-testid="profile-view-mobile"
              >
                <Smartphone className="w-5 h-5" />
                <div className="text-left">
                  <p className="text-sm font-medium">Mobile</p>
                  <p className="text-[10px] opacity-70">Auto-fit to screen</p>
                </div>
              </button>
              <button
                onClick={() => {
                  localStorage.setItem('writeright_desktop_view', 'true');
                  persistProfilePref({ force_desktop_view: true });
                  const viewport = document.querySelector('meta[name="viewport"]');
                  if (viewport) viewport.setAttribute('content', 'width=1200, initial-scale=0.5, maximum-scale=2');
                  document.body.classList.add('force-desktop-view');
                  window.dispatchEvent(new CustomEvent('viewmode-changed', { detail: true }));
                  window.dispatchEvent(new Event('resize'));
                }}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  localStorage.getItem('writeright_desktop_view') === 'true'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/30'
                }`}
                data-testid="profile-view-desktop"
              >
                <Monitor className="w-5 h-5" />
                <div className="text-left">
                  <p className="text-sm font-medium">Desktop</p>
                  <p className="text-[10px] opacity-70">Full 1200px layout</p>
                </div>
              </button>
            </div>
          </div>
          
          {/* Default Letter Style */}
          <div className="space-y-2">
            <Label className="text-foreground font-medium flex items-center gap-2">
              Default Letter Style
              <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">Preference</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Your preferred writing style for admin letters and correspondence
            </p>
            <select 
              value={providerLetterTone || 'professional'} 
              onChange={(e) => setProviderLetterTone(e.target.value)}
              className="w-full h-12 px-3 rounded-md border border-border bg-input text-base"
              style={{ fontSize: '16px' }}
              data-testid="profile-letter-style"
            >
              <option value="brief" style={{ padding: '12px', fontSize: '16px' }}>Brief - 1-2 sentences</option>
              <option value="concise" style={{ padding: '12px', fontSize: '16px' }}>Concise - 1 paragraph</option>
              <option value="professional" style={{ padding: '12px', fontSize: '16px' }}>Professional - Standard format</option>
              <option value="detailed" style={{ padding: '12px', fontSize: '16px' }}>Detailed - Full comprehensive</option>
            </select>
          </div>
          
          {/* Default Mood Theme moved to the top "Display & Theme" block
              (Feb 28, 2026 v6.8.40). */}

          {/* ===== PERSONAL INFO (BOTTOM) ===== */}
          <div className="pt-3 border-t border-border">
            <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wider">Personal Info</p>
          </div>
          
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              Signature Block
            </Label>
            <p className="text-xs text-muted-foreground">
              This will be automatically added to your generated admin letters
            </p>
            <textarea
              value={profileSignature}
              onChange={(e) => setProfileSignature(e.target.value)}
              placeholder={"LAKIA S. STEWART, DNP, FNP-C, WHNP-BC\nMAJ, AN\nFamily & Women's Health Nurse Practitioner\nPrimary Care Manager\nMoncrief Army Health Clinic"}
              className="w-full min-h-[120px] px-3 py-2 rounded-md border border-border bg-input text-sm font-mono"
              data-testid="profile-signature"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm">Your Rank</Label>
              <select 
                value={profileRank} 
                onChange={(e) => setProfileRank(e.target.value)}
                className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
                data-testid="profile-rank"
              >
                <option value="">Select rank</option>
                {ranks.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Default MTF</Label>
              <input
                value={profileMTF}
                onChange={(e) => setProfileMTF(e.target.value)}
                placeholder="e.g., Womack AMC"
                className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
                data-testid="profile-mtf"
              />
            </div>
          </div>
          
          {/* Letterhead for Exported Letters */}
          <div className="space-y-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <Label className="text-foreground font-medium flex items-center gap-2">
              Letterhead
              <span className="text-xs px-2 py-0.5 bg-primary/20 text-primary rounded">Exports</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Centered header on exported PDF/Word letters. Leave blank if not needed.
            </p>
            <input
              value={letterheadLine1 || ''}
              onChange={(e) => setLetterheadLine1(e.target.value)}
              placeholder="DEPARTMENT OF THE ARMY"
              className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm font-mono uppercase"
              data-testid="letterhead-line1"
            />
            <input
              value={letterheadLine2 || ''}
              onChange={(e) => setLetterheadLine2(e.target.value)}
              placeholder="HEADQUARTERS, UNITED STATES ARMY MEDICAL DEPARTMENT ACTIVITY"
              className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm font-mono uppercase"
              data-testid="letterhead-line2"
            />
            <textarea
              value={letterheadAddress || ''}
              onChange={(e) => setLetterheadAddress(e.target.value)}
              placeholder={"4500 8TH DIVISION ROAD\nFORT JACKSON, SOUTH CAROLINA 29207-5700"}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-border bg-input text-sm font-mono uppercase"
              data-testid="letterhead-address"
            />
          </div>
          
          {/* Personal Info for Letter Auto-Fill - Only shown in Patient Mode */}
          {userRole === 'patient' && (
          <div className="space-y-3 p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-sm font-medium text-green-500">
              <FileText className="w-4 h-4" />
              Personal Info (Auto-fills in "I Need a Letter")
            </div>
            <p className="text-xs text-muted-foreground">
              Save your info here so it auto-fills when you use patient mode
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">First Name</Label>
                <input
                  value={personalFirstName || ''}
                  onChange={(e) => setPersonalFirstName && setPersonalFirstName(e.target.value)}
                  placeholder="Your first name"
                  className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
                  data-testid="profile-first-name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Last Name</Label>
                <input
                  value={personalLastName || ''}
                  onChange={(e) => setPersonalLastName && setPersonalLastName(e.target.value)}
                  placeholder="Your last name"
                  className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
                  data-testid="profile-last-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date of Birth</Label>
                <input
                  type="date"
                  value={personalDOB || ''}
                  onChange={(e) => setPersonalDOB && setPersonalDOB(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
                  data-testid="profile-dob"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Phone</Label>
                <input
                  type="tel"
                  value={personalPhone || ''}
                  onChange={(e) => setPersonalPhone && setPersonalPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
                  data-testid="profile-phone"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Occupation / Job Title</Label>
              <input
                value={personalOccupation || ''}
                onChange={(e) => setPersonalOccupation && setPersonalOccupation(e.target.value)}
                placeholder="e.g., Warehouse Associate, Office Manager"
                className="w-full h-9 px-3 rounded-md border border-border bg-input text-sm"
                data-testid="profile-occupation"
              />
            </div>
          </div>
          )}

          {/* Saved Medical History / Injuries (Patient Mode — v6.8.44)
              Free-form list of past conditions/injuries with year, so the
              user can one-tap inject them into the dictation/situation
              field when drafting letters or disability appeals. Server-
              persisted so it follows the user across devices. */}
          {userRole === 'patient' && (
            <SavedInjuriesEditor
              savedInjuries={savedInjuries}
              setSavedInjuries={setSavedInjuries}
            />
          )}

          <div className="pt-3 border-t border-border">
            <Button 
              onClick={onSave} 
              disabled={isSaving}
              className="w-full"
            >
              {isSaving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
